// Permissões por função (seção 12.4). admin tem todas.
export const ROLES = ["admin", "seller", "teacher"] as const;
export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  "leads.read",
  "leads.write",
  "enrollment_links.create",
  "finance.seller.read",
  "availability.read",
  "availability.write",
  "students.read",
  "students.update",
  "scenarios.read",
  "scenarios.write",
  "simulations.read",
  "simulations.write",
  "finance.teacher.read",
  "admin.metrics",
  "admin.logs",
  "admin.users",
  "admin.students",
  "admin.billing",
  "admin.settings",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ROLE_GRANTS: Record<Role, string[]> = {
  admin: ["*"],
  seller: ["leads.*", "enrollment_links.create", "finance.seller.read"],
  teacher: [
    "availability.*",
    "students.read",
    "students.update",
    "scenarios.*",
    "simulations.*",
    "finance.teacher.read",
  ],
};

function grantMatches(grant: string, permission: string): boolean {
  if (grant === "*") return true;
  if (grant.endsWith(".*")) return permission.startsWith(grant.slice(0, -1));
  return grant === permission;
}

export function permissionsFor(roles: readonly Role[]): Set<Permission> {
  const out = new Set<Permission>();
  for (const p of PERMISSIONS) {
    if (roles.some((r) => ROLE_GRANTS[r].some((g) => grantMatches(g, p)))) out.add(p);
  }
  return out;
}

export function hasPermission(roles: readonly Role[], permission: Permission): boolean {
  return roles.some((r) => ROLE_GRANTS[r].some((g) => grantMatches(g, permission)));
}

/**
 * Ninguém concede acesso maior que o próprio nem altera o próprio acesso.
 */
export function canAssignRoles(
  actor: { id: string; roles: readonly Role[] },
  target: { id: string },
  newRoles: readonly Role[],
): { ok: true } | { ok: false; reason: "self_change" | "escalation" } {
  if (actor.id === target.id) return { ok: false, reason: "self_change" };
  const actorPerms = permissionsFor(actor.roles);
  for (const p of permissionsFor(newRoles)) {
    if (!actorPerms.has(p)) return { ok: false, reason: "escalation" };
  }
  return { ok: true };
}

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  seller: "Vendedora",
  teacher: "Professor",
};
